import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API = '/api';

const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    plus: ['M12 5v14', 'M5 12h14'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    x: ['M18 6 6 18', 'M6 6l12 12'],
    dots: ['M12 12m-1 0a1 1 0 1 0 2 0 1 1 0 1 0-2 0', 'M12 5m-1 0a1 1 0 1 0 2 0 1 1 0 1 0-2 0', 'M12 19m-1 0a1 1 0 1 0 2 0 1 1 0 1 0-2 0'],
    up: ['M18 15l-6-6-6 6'],
    down: ['M6 9l6 6 6-6'],
    tripOperations: ['M3 11l19-9-9 19-2-8-8-2z']
};

export default function RouteStops({ user }) {
    const [routes, setRoutes] = useState([]);
    const [selectedRouteId, setSelectedRouteId] = useState('');
    const [stops, setStops] = useState([]);
    const [loadingStops, setLoadingStops] = useState(false);
    const [modal, setModal] = useState(null); // { type: 'add'|'edit', data }

    const operatorId = user?.operatorId || user?._id || 'all';

    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const routeLayer = useRef(null);
    const stopMarkers = useRef([]);
    const stopsRef = useRef([]);
    const selectedRouteIdRef = useRef('');

    useEffect(() => { stopsRef.current = stops; }, [stops]);
    useEffect(() => { selectedRouteIdRef.current = selectedRouteId; }, [selectedRouteId]);

    useEffect(() => {
        fetchRoutes();
        initMap();
    }, []);

    const fetchRoutes = async () => {
        try {
            const res = await axios.get(`${API}/routes/${operatorId}`);
            setRoutes(res.data);
        } catch (err) { console.error(err); }
    };

    const fetchStops = async (routeId) => {
        if (!routeId) {
            setStops([]);
            return;
        }
        setLoadingStops(true);
        try {
            const res = await axios.get(`${API}/route-stops/${routeId}`);
            setStops(res.data);
        } catch (err) { console.error(err); }
        finally { setLoadingStops(false); }
    };

    const initMap = () => {
        if (!mapInstance.current && mapRef.current) {
            mapInstance.current = L.map(mapRef.current).setView([20.5937, 78.9629], 5);
            L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
                attribution: '&copy; OpenStreetMap contributors'
            }).addTo(mapInstance.current);

            mapInstance.current.on('click', async (e) => {
                if (!selectedRouteIdRef.current) return;
                const lat = e.latlng.lat;
                const lng = e.latlng.lng;
                
                let name = `Location (${lat.toFixed(4)}, ${lng.toFixed(4)})`;
                try {
                    const res = await axios.get(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
                    if (res.data && res.data.display_name) {
                        name = res.data.display_name;
                    }
                } catch (err) { console.error("Reverse geocode failed", err); }
                
                setModal({ type: 'add', data: { lat, lng, name, order: stopsRef.current.length } });
            });
        }
    };

    useEffect(() => {
        if (selectedRouteId) {
            fetchStops(selectedRouteId);
        } else {
            if (routeLayer.current && mapInstance.current) {
                mapInstance.current.removeLayer(routeLayer.current);
                routeLayer.current = null;
            }
            setStops([]);
            clearMarkers();
        }
    }, [selectedRouteId]);

    useEffect(() => {
        const drawRoute = async () => {
            if (selectedRouteId && routes.length > 0 && mapInstance.current) {
                const route = routes.find(r => r._id === selectedRouteId);
                if (!route) return;

                // Build coordinates array: Pickup -> stops -> Drop
                const coords = [];
                if (route.pickupLng && route.pickupLat) coords.push(`${route.pickupLng},${route.pickupLat}`);
                
                const sortedStops = [...stops].sort((a,b) => a.order - b.order);
                sortedStops.forEach(s => {
                    if (s.lng && s.lat) coords.push(`${s.lng},${s.lat}`);
                });

                if (route.dropLng && route.dropLat) coords.push(`${route.dropLng},${route.dropLat}`);

                if (coords.length < 2) return;

                try {
                    const url = `https://router.project-osrm.org/route/v1/driving/${coords.join(';')}?overview=full&geometries=geojson`;
                    const response = await axios.get(url);
                    
                    if (response.data && response.data.routes && response.data.routes.length > 0) {
                        const routeData = response.data.routes[0];
                        if (routeLayer.current) mapInstance.current.removeLayer(routeLayer.current);
                        const geojsonRoute = { type: "Feature", properties: {}, geometry: routeData.geometry };
                        routeLayer.current = L.geoJSON(geojsonRoute, {
                            style: { color: '#16539C', weight: 5, opacity: 0.7 }
                        }).addTo(mapInstance.current);
                        mapInstance.current.fitBounds(routeLayer.current.getBounds(), { padding: [50, 50] });
                    }
                } catch (err) {
                    console.error("OSRM Error", err);
                }
            } else if (!selectedRouteId) {
                if (routeLayer.current && mapInstance.current) {
                    mapInstance.current.removeLayer(routeLayer.current);
                    routeLayer.current = null;
                }
            }
        };

        const timer = setTimeout(drawRoute, 500);
        return () => clearTimeout(timer);
    }, [selectedRouteId, routes, stops]);

    useEffect(() => {
        updateMarkers();
    }, [stops]);

    const clearMarkers = () => {
        if (mapInstance.current) {
            stopMarkers.current.forEach(m => mapInstance.current.removeLayer(m));
        }
        stopMarkers.current = [];
    };

    const updateMarkers = () => {
        clearMarkers();
        if (!mapInstance.current) return;

        stops.forEach((stop, index) => {
            const marker = L.circleMarker([stop.lat, stop.lng], {
                radius: 8,
                fillColor: "#16539C",
                fillOpacity: 1,
                color: "#ffffff",
                weight: 2,
            })
            .bindTooltip(`${index + 1}. ${stop.name}`, { permanent: false, direction: 'top' })
            .on('click', () => {
                setModal({ type: 'edit', data: stop });
            })
            .addTo(mapInstance.current);

            stopMarkers.current.push(marker);
        });
    };

    const handleSaveStop = async (formData) => {
        try {
            const payload = {
                ...formData,
                lat: Number(formData.lat),
                lng: Number(formData.lng),
                routeId: selectedRouteId,
                operatorId
            };
            if (modal.type === 'edit') {
                await axios.put(`${API}/route-stops/${modal.data._id}`, payload);
            } else {
                await axios.post(`${API}/route-stops`, payload);
            }
            setModal(null);
            fetchStops(selectedRouteId);
        } catch (err) {
            console.error(err);
            alert(`Failed to save stop: ${err.response?.data?.message || err.message}`);
        }
    };

    const handleDeleteStop = async (id) => {
        if (!window.confirm("Are you sure?")) return;
        try {
            await axios.delete(`${API}/route-stops/${id}`);
            fetchStops(selectedRouteId);
        } catch (err) { console.error(err); }
    };

    const reorder = async (index, direction) => {
        const newStops = [...stops];
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= newStops.length) return;

        [newStops[index], newStops[targetIndex]] = [newStops[targetIndex], newStops[index]];
        setStops(newStops);

        const updates = newStops.map((s, i) => ({ _id: s._id, order: i }));
        try {
            await axios.put(`${API}/route-stops/reorder/bulk`, { stops: updates });
        } catch (err) { console.error(err); }
    };

    return (
        <div className="md-page">
            <div className="md-header">
                <div>
                    <h1 className="md-title">Route Stops Management</h1>
                    <p className="md-subtitle">Manage intermediate stops along your main transport routes.</p>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(400px, 1fr) 1.5fr', gap: '24px', marginTop: '20px' }}>
                <div className="md-panel" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 200px)' }}>
                    <div className="md-form-group" style={{ padding: '16px' }}>
                        <label className="md-label">Select Main Route</label>
                        <select
                            className="md-input"
                            value={selectedRouteId}
                            onChange={e => setSelectedRouteId(e.target.value)}
                        >
                            <option value="">-- Choose a Route --</option>
                            {routes.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                        </select>
                    </div>

                    {selectedRouteId && (
                        <>
                            <div className="md-panel-toolbar" style={{ borderTop: '1px solid #f1f5f9', padding: '12px 16px' }}>
                                <span style={{ fontWeight: 600, fontSize: '14px' }}>Route Stops ({stops.length})</span>
                                <button className="md-add-btn" onClick={() => setModal({ type: 'add', data: { name: '', order: stops.length } })} style={{ padding: '6px 12px', fontSize: '12px' }}>
                                    <Icon d={icons.plus} size={12} /> Add Stop
                                </button>
                            </div>

                            <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
                                {loadingStops ? (
                                    <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Loading stops...</div>
                                ) : modal ? (
                                    <InlineStopForm
                                        key={modal.data?._id || `new-${modal.data?.lat}-${modal.data?.lng}`}
                                        type={modal.type}
                                        data={modal.data}
                                        onClose={() => setModal(null)}
                                        onSave={handleSaveStop}
                                    />
                                ) : stops.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>No stops added yet. Click on the map or use the button to add.</div>
                                ) : (
                                    <div className="stops-list" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {stops.map((stop, i) => (
                                            <div key={stop._id} className="stop-card" style={{
                                                display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#fff',
                                                border: '1px solid #e2e8f0', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                                transition: 'all 0.2s'
                                            }}>
                                                <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#16539C', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 600 }}>
                                                    {i + 1}
                                                </div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{stop.name}</div>
                                                    <div style={{ fontSize: '11px', color: '#64748b' }}>{stop.lat.toFixed(6)}, {stop.lng.toFixed(6)}</div>
                                                </div>
                                                <div className="stop-actions" style={{ display: 'flex', gap: '4px' }}>
                                                    <button className="md-icon-btn" onClick={() => reorder(i, -1)} disabled={i === 0} title="Move Up"><Icon d={icons.up} size={14} /></button>
                                                    <button className="md-icon-btn" onClick={() => reorder(i, 1)} disabled={i === stops.length - 1} title="Move Down"><Icon d={icons.down} size={14} /></button>
                                                    <button className="md-icon-btn" onClick={() => setModal({ type: 'edit', data: stop })} title="Edit"><Icon d={icons.edit} size={14} /></button>
                                                    <button className="md-icon-btn md-icon-btn-danger" onClick={() => handleDeleteStop(stop._id)} title="Delete"><Icon d={icons.trash} size={14} /></button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                <div className="map-panel" style={{ display: 'flex', flexDirection: 'column', gap: '16px', position: 'relative' }}>
                    <div ref={mapRef} style={{ flex: 1, background: '#f1f5f9', borderRadius: '20px', minHeight: '600px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                    </div>
                    {!selectedRouteId && (
                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.8)', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', borderRadius: '20px' }}>
                            <div style={{ textAlign: 'center', padding: '32px', background: '#fff', borderRadius: '16px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', maxWidth: '300px' }}>
                                <div style={{ background: '#eef2ff', width: '64px', height: '64px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                                    <Icon d={icons.tripOperations} size={32} style={{ color: '#16539C' }} />
                                </div>
                                <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>Select a Route</h3>
                                <p style={{ color: '#64748b', fontSize: '14px', marginTop: '8px' }}>Select a main route from the dropdown to start adding and managing stops.</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function InlineStopForm({ type, data, onClose, onSave }) {
    const [form, setForm] = useState({
        name: data.name || '',
        lat: data.lat || '',
        lng: data.lng || '',
        order: data.order || 0
    });

    const [query, setQuery] = useState(data.name || '');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const searchLocation = async (q) => {
        if (!q || q.length < 3) return [];
        try {
            const res = await axios.get(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=5`);
            return res.data;
        } catch (error) { return []; }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            if (query && query !== form.name) {
                const results = await searchLocation(query);
                setSuggestions(results);
                setShowSuggestions(true);
            } else {
                setSuggestions([]);
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [query, form.name]);

    const handleSelectLocation = (place) => {
        const lat = parseFloat(place.lat);
        const lon = parseFloat(place.lon);
        const displayName = place.display_name;

        setForm(prev => ({ ...prev, name: displayName, lat, lng: lon }));
        setQuery(displayName);
        setShowSuggestions(false);
    };

    return (
        <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', animation: 'fadeIn 0.3s ease' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 700, margin: 0, color: '#1e293b' }}>
                    {type === 'edit' ? 'Edit Stop' : 'Add New Stop'}
                </h3>
                <button onClick={onClose} style={{ border: 'none', background: 'none', padding: 4, cursor: 'pointer', color: '#64748b' }}>
                    <Icon d={icons.x} size={16} />
                </button>
            </div>

            <div className="md-form-group" style={{ position: 'relative' }}>
                <label className="md-label">Stop Location Search*</label>
                <div style={{ position: 'relative' }}>
                    <input
                        className="md-input"
                        placeholder="Search for a location..."
                        value={query}
                        onChange={e => {
                            setQuery(e.target.value);
                            setForm({ ...form, name: e.target.value });
                        }}
                        onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                        style={{ paddingLeft: '32px' }}
                    />
                    <div style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                        <Icon d={icons.search} size={14} />
                    </div>
                </div>
                {showSuggestions && suggestions.length > 0 && (
                    <ul style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', zIndex: 1000, margin: 0, padding: 0, listStyle: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto' }}>
                        {suggestions.map(place => (
                            <li key={place.place_id} onClick={() => handleSelectLocation(place)} style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: '12px', color: '#334155' }}>
                                {place.display_name}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                <div className="md-form-group">
                    <label className="md-label">Latitude*</label>
                    <input className="md-input" type="number" step="any" value={form.lat} onChange={e => setForm({ ...form, lat: e.target.value })} placeholder="Lat" />
                </div>
                <div className="md-form-group">
                    <label className="md-label">Longitude*</label>
                    <input className="md-input" type="number" step="any" value={form.lng} onChange={e => setForm({ ...form, lng: e.target.value })} placeholder="Lng" />
                </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
                <button
                    className="md-cancel-btn"
                    onClick={onClose}
                    style={{ flex: 1, padding: '8px', fontSize: '13px' }}
                >
                    Cancel
                </button>
                <button
                    className="md-save-btn"
                    onClick={() => onSave(form)}
                    disabled={!form.name || form.lat === '' || form.lng === ''}
                    style={{ flex: 2, padding: '8px', fontSize: '13px', opacity: (!form.name || form.lat === '' || form.lng === '') ? 0.7 : 1 }}
                >
                    {type === 'edit' ? 'Update Stop' : 'Save Stop'}
                </button>
            </div>
        </div>
    );
}
